import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router'
import { ApiFehler, hole, schicke } from '../lib/api'
import { Button, Card, TextField } from '../ui'

type Zugang = 'child' | 'guest' | 'invite'

/**
 * Eltern melden sich mit ihrem Google-Konto an. Kinder und Gäste erhalten
 * eigene, klar benannte Einstiege, obwohl beide denselben Code-Weg nutzen.
 */
export function Anmeldung() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [parameter] = useSearchParams()
  const einladungsCode = parameter.get('code')?.trim().toUpperCase() ?? ''
  // Der Elternweg bleibt sichtbar. Aktiv ist sein Knopf aber nur, wenn der
  // Server ihn wirklich eingerichtet hat; sonst fuehrt er nicht ins Leere.
  const wege = useQuery({
    queryKey: ['providers'],
    queryFn: () => hole<{ google: boolean; memberCode: boolean }>('/api/auth/providers'),
    staleTime: Infinity,
  })
  const [zugang, setZugang] = useState<Zugang | null>(
    parameter.get('zugang') === 'invite' && einladungsCode ? 'invite' : null,
  )
  const googleEinladung = zugang === 'invite' && parameter.get('google') === '1'
  const [code, setCode] = useState(einladungsCode)
  const [pin, setPin] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const anmelden = async (): Promise<void> => {
    setFehler(null)
    setLaeuft(true)
    try {
      await schicke('POST', '/api/auth/member-code', { code, pin })
      if (googleEinladung && wege.data?.google) {
        window.location.assign('/api/auth/google/link')
        return
      }
      void navigate('/', { replace: true })
      await qc.invalidateQueries({ queryKey: ['me'] })
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Anmeldung fehlgeschlagen.')
    } finally {
      setLaeuft(false)
    }
  }

  const titel =
    zugang === 'child'
      ? 'Als Kind anmelden'
      : zugang === 'invite'
        ? googleEinladung
          ? 'Google-Einladung annehmen'
          : 'Einladung annehmen'
        : 'Als Gast anmelden'
  const erklaerung =
    zugang === 'child'
      ? 'Gib den Code und die PIN ein, die deine Eltern für dich erstellt haben.'
      : zugang === 'invite'
        ? googleEinladung
          ? 'Bestätige die Einladung mit der separat erhaltenen PIN. Danach verbindest du dein persönliches Google-Konto.'
          : 'Der Code ist schon eingetragen. Ergänze nur noch die sechsstellige PIN, die du separat erhalten hast.'
        : 'Gib den Code und die PIN ein, die dir deine Familie gegeben hat.'

  return (
    <main className="inhalt anmeldung">
      <div className="anmeldung-kopf">
        <h1>Famora</h1>
        <p>Der Familienalltag an einem Ort.</p>
      </div>

      {zugang === null ? (
        <section className="anmeldung-auswahl" aria-labelledby="zugang-frage">
          <h2 id="zugang-frage">Wie möchtest du dich anmelden?</h2>
          <div className="zugangsliste">
            <Card titel="Eltern">
              <p className="unterzeile">Mit deinem persönlichen Google-Konto.</p>
              {wege.data?.google ? (
                <a className="btn btn--filled" href="/api/auth/google/start">
                  <span className="btn__text">Mit Google anmelden</span>
                </a>
              ) : (
                <>
                  <Button disabled aria-describedby="google-hinweis">
                    Mit Google anmelden
                  </Button>
                  <p id="google-hinweis" className="unterzeile">
                    {wege.isPending
                      ? 'Verfügbarkeit wird geprüft …'
                      : 'Die Google-Anmeldung ist noch nicht eingerichtet.'}
                  </p>
                </>
              )}
            </Card>

            <Card titel="Kinder">
              <p className="unterzeile">Mit deinem eigenen Code und deiner sechsstelligen PIN.</p>
              <Button
                variant="tonal"
                onClick={() => {
                  setZugang('child')
                }}
              >
                Als Kind weiter
              </Button>
            </Card>

            <Card titel="Gäste">
              <p className="unterzeile">
                Zum Beispiel für Großeltern, Babysitter oder andere Helfende.
              </p>
              <Button
                variant="tonal"
                onClick={() => {
                  setZugang('guest')
                }}
              >
                Als Gast weiter
              </Button>
            </Card>
          </div>
        </section>
      ) : (
        <section className="anmeldung-code" aria-labelledby="code-titel">
          <Button
            variant="text"
            onClick={() => {
              setZugang(null)
              setCode('')
              void navigate('/', { replace: true })
            }}
          >
            Zurück zur Auswahl
          </Button>
          <h2 id="code-titel">{titel}</h2>
          <p>{erklaerung}</p>

          <form
            className="stack-4"
            onSubmit={(e) => {
              e.preventDefault()
              void anmelden()
            }}
          >
            <TextField
              id="code"
              label="Code"
              wert={code}
              onWert={setCode}
              className="code-feld"
              name="code"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
            />
            <TextField
              id="pin"
              label="PIN, sechs Ziffern"
              wert={pin}
              onWert={(w) => {
                setPin(w.replace(/\D/g, '').slice(0, 6))
              }}
              className="code-feld"
              name="pin"
              inputMode="numeric"
              autoComplete="off"
              autoFocus={zugang === 'invite'}
              pattern="\d{6}"
              required
            />
            {fehler ? (
              <p className="fehler" role="alert">
                {fehler}
              </p>
            ) : null}
            <Button type="submit" laeuft={laeuft} disabled={pin.length !== 6 || code.length < 6}>
              {laeuft
                ? 'Wird geprüft …'
                : googleEinladung && wege.data?.google
                  ? 'PIN bestätigen und mit Google weiter'
                  : 'Anmelden'}
            </Button>
            {googleEinladung && wege.data && !wege.data.google ? (
              <p className="unterzeile">
                Google ist gerade nicht verfügbar. Du kannst die Einladung trotzdem mit Code und PIN
                annehmen.
              </p>
            ) : null}
          </form>
        </section>
      )}
    </main>
  )
}
